import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastWarning } from '~/utils/useToastWarning';
import { Button, BUTTON_STYLES, Pagination } from '~/ui';
import PhotoPicker from '~/components/AnnouncementForm/PhotoPickerContainer/PhotoPicker/PhotoPicker';
import { IPhoto as IPhotoPicker } from '~/components/AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import FolderIcon from '~/assets/svg/newSvg/folder.svg?react';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import styles from './PhotoPickerWithUpload.module.scss';

export interface IPhoto {
  id: number;
  full_photo_url: string;
  path?: string;
}

interface IUploadedStoragePhoto {
  id: number;
  photo_id: number;
  folder_id: number | null;
  url: string;
  full_photo_url: string;
}

interface IAsset {
  asset_id: number;
  asset_name: string;
  photos_count: number;
}

interface IEntity {
  entity_id: number;
  entity_name: string;
  entity_section?: string | null;
  photos_count: number;
}

interface IFolder {
  id: number;
  name: string;
  parent_id: number | null;
  photos_count: number;
  children_count: number;
}

interface IFolderStackItem {
  id: number;
  name: string;
}

interface SelectedPage {
  selected: number;
}

interface PhotoPickerWithUploadProps {
  module: string;
  assetId: number | null;
  selectedImages: IPhoto[];
  onImageChange: (image: IPhoto) => void;
  onCloseClick: () => void;
  onResetClick: () => void;
  initialPhotos?: IPhoto[];
  maxImages?: number;
}

const SUPPORTS_FOLDERS = ['announcement', 'knowledge_base', 'promo_loyalty', 'food', 'ifcm_wares'];
const SUPPORTS_ENTITIES = ['accommodation', 'food'];
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_UPLOAD_AT_ONCE = 10;

const PhotoPickerWithUpload = ({
  module,
  assetId,
  selectedImages,
  onImageChange,
  onCloseClick,
  onResetClick,
  maxImages,
}: PhotoPickerWithUploadProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const toastWarning = useToastWarning();
  const client = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [page, setPage] = useState(1);

  // Super-admin: when assetId prop is null, let user pick an asset first
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [selectedAssetName, setSelectedAssetName] = useState('');
  const [assetsPage, setAssetsPage] = useState(1);
  const ASSETS_PER_PAGE = 9;
  const effectiveAssetId = assetId ?? selectedAssetId;
  const isSuperAdminMode = assetId === null; // parent didn't provide assetId

  // Entity navigation (for accommodation, food — have sub-entities)
  const supportsEntities = SUPPORTS_ENTITIES.includes(module);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState('');

  const handleSelectAsset = (asset: IAsset) => {
    setSelectedAssetId(asset.asset_id);
    setSelectedAssetName(asset.asset_name);
    setSelectedEntityId(null);
    setSelectedEntityName('');
    setFolderStack([]);
    setPage(1);
  };

  const handleBackToAssets = () => {
    setSelectedAssetId(null);
    setSelectedAssetName('');
    setSelectedEntityId(null);
    setSelectedEntityName('');
    setFolderStack([]);
    setPage(1);
  };

  const handleSelectEntity = (entity: IEntity) => {
    setSelectedEntityId(entity.entity_id);
    setSelectedEntityName(entity.entity_name);
    setFolderStack([]);
    setPage(1);
  };

  const handleBackToEntities = () => {
    setSelectedEntityId(null);
    setSelectedEntityName('');
    setFolderStack([]);
    setPage(1);
  };

  // Folder navigation
  const supportsFolders = SUPPORTS_FOLDERS.includes(module);
  const [folderStack, setFolderStack] = useState<IFolderStackItem[]>([]);
  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null;

  const enterFolder = (folder: IFolder) => {
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    setPage(1);
  };

  const navigateToBreadcrumb = (index: number) => {
    setFolderStack(prev => prev.slice(0, index));
    setPage(1);
  };

  const photosParams: Record<string, string | number> = { perPage: 9, page };
  if (supportsFolders) photosParams.folder_id = currentFolderId ?? '';
  if (supportsEntities && selectedEntityId !== null) photosParams.entity_id = selectedEntityId;

  const photosQueryKey = ['photo-storage', module, effectiveAssetId, photosParams];
  const foldersQueryKey = ['photo-storage-folders-picker', module, effectiveAssetId, currentFolderId];

  // Assets query — only when parent doesn't provide assetId (super admin)
  const { data: assets = [], isLoading: assetsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(`admin/photo-storage/${module}/assets`);
      return data as IAsset[];
    },
    queryKey: ['photo-storage-assets-picker', module],
    enabled: isSuperAdminMode,
    onError: (error: unknown) => toastError(error),
  });

  // Entities query (for accommodation, food)
  const { data: entities = [], isLoading: entitiesLoading } = useQuery({
    queryFn: async () => {
      if (!effectiveAssetId) return [];
      const { data } = await instance.get(`admin/photo-storage/${module}/${effectiveAssetId}/entities`);
      return data as IEntity[];
    },
    queryKey: ['photo-storage-entities-picker', module, effectiveAssetId],
    enabled: !!effectiveAssetId && supportsEntities,
    onError: (error: unknown) => toastError(error),
  });

  // Folders query
  const { data: folders = [], isLoading: foldersLoading } = useQuery({
    queryFn: async () => {
      if (!effectiveAssetId) return [];
      const folderParams: Record<string, string> = {};
      if (currentFolderId !== null) folderParams.parent_id = String(currentFolderId);
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${effectiveAssetId}/folders`,
        { params: folderParams }
      );
      return data as IFolder[];
    },
    queryKey: foldersQueryKey,
    enabled: !!effectiveAssetId && supportsFolders && (!supportsEntities || !!selectedEntityId),
    onError: (error: unknown) => toastError(error),
  });

  // Photos query
  const {
    data: storagePhotos,
    isFetching,
    isLoading,
  } = useQuery({
    queryFn: async () => {
      if (!effectiveAssetId) return { data: [], last_page: 1 };
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${effectiveAssetId}`,
        { params: photosParams }
      );
      return data;
    },
    queryKey: photosQueryKey,
    enabled: !!effectiveAssetId && (!supportsEntities || !!selectedEntityId),
  });

  // Upload to photo-storage (always — effectiveAssetId is always set when upload is shown)
  const { mutateAsync: uploadToStorage } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      if (supportsFolders && currentFolderId !== null) {
        formData.append('folder_id', String(currentFolderId));
      }
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${effectiveAssetId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) {
              // Cap at 95% — last 5% is server-side processing
              setUploadProgress(Math.round((e.loaded * 95) / e.total));
            }
          },
        }
      );
      return data as IUploadedStoragePhoto[];
    },
    {
      onSuccess: (uploaded) => {
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 600);
        client.invalidateQueries(photosQueryKey);
        client.invalidateQueries(foldersQueryKey);
        uploaded.forEach(photo => {
          onImageChange({ id: photo.photo_id, full_photo_url: photo.url });
        });
        toastSuccess(t('photo_uploaded'));
      },
      onError: (error: unknown) => {
        setUploadProgress(0);
        toastError(error);
      },
    }
  );

  const runUpload = async (files: File[]) => {
    const imageFiles = files.filter(f => ALLOWED_MIME_TYPES.includes(f.type));
    if (files.length > imageFiles.length) toastWarning(t('only_images_allowed'));
    if (!imageFiles.length) return;
    const batch = imageFiles.slice(0, MAX_UPLOAD_AT_ONCE);
    if (imageFiles.length > MAX_UPLOAD_AT_ONCE) toastWarning(t('upload_limit', { max: MAX_UPLOAD_AT_ONCE }));
    setIsUploading(true);
    setUploadProgress(0);
    try {
      await uploadToStorage(batch);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    await runUpload(Array.from(files));
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (!effectiveAssetId) return;
    await runUpload(Array.from(e.dataTransfer.files));
  };

  const allPhotos: IPhotoPicker[] = (storagePhotos?.data || []).map(
    (photo: { id: number; photo_id: number; url: string }) => ({
      id: photo.photo_id,
      full_photo_url: photo.url,
      path: '',
    })
  );

  const selectedImagesWithPath: IPhotoPicker[] = selectedImages.map(img => ({
    ...img,
    path: img.path || '',
  }));

  // View state
  const showAssetPicker = isSuperAdminMode && !selectedAssetId;
  const showEntityPicker = !showAssetPicker && supportsEntities && !!effectiveAssetId && !selectedEntityId;
  const assetsPageCount = Math.ceil(assets.length / ASSETS_PER_PAGE);
  const pagedAssets = assets.slice((assetsPage - 1) * ASSETS_PER_PAGE, assetsPage * ASSETS_PER_PAGE);

  return (
    <div
      className={styles.container}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleFileDrop}
    >
      {isDragging && (
        <div className={styles.dropOverlay}>
          <PhotoIcon className={styles.dropOverlayIcon} />
          <p className={styles.dropOverlayText}>{t('drop_photos_here')}</p>
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.title}>{t('choose_photo')}</span>
          {selectedImages.length > 0 && (
            <span className={styles.subtitle}>
              {t('selected')}: {selectedImages.length}
              {maxImages && ` / ${maxImages}`}
            </span>
          )}
        </div>
        <div className={styles.buttons}>
          {selectedImages.length > 0 && (
            <Button
              className={styles.headerBtn}
              buttonStyle={BUTTON_STYLES.EMPTY}
              text={t('discard')}
              onClick={onResetClick}
              color="red"
            />
          )}
          <Button
            className={styles.headerBtn}
            buttonStyle={BUTTON_STYLES.EMPTY}
            type="button"
            text={t('close')}
            onClick={onCloseClick}
          />
        </div>
      </div>

      {/* Max photos warning */}
      {maxImages && selectedImages.length >= maxImages && (
        <div className={styles.maxWarning}>
          {t('max_photos_selected', { max: maxImages })}
        </div>
      )}

      {/* Super-admin: asset selection */}
      {showAssetPicker && (
        <>
          <div className={styles.assetSection}>
            {assetsLoading
              ? Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className={styles.folderShimmer} style={{ animationDelay: `${i * 0.05}s` }} />
                ))
              : pagedAssets.map(asset => (
                  <button
                    key={asset.asset_id}
                    type="button"
                    className={styles.folderCard}
                    onClick={() => handleSelectAsset(asset)}
                  >
                    <FolderIcon className={styles.folderIcon} />
                    <span className={styles.folderName}>{asset.asset_name}</span>
                    <span className={styles.folderCount}>
                      {asset.photos_count} {t('photos_short')}
                    </span>
                  </button>
                ))}
          </div>
          <div className={styles.paginationContainer}>
            <Pagination
              pageCount={assetsPageCount}
              onPageChange={(p: SelectedPage) => setAssetsPage(p.selected + 1)}
            />
          </div>
          {selectedImages.length > 0 && (
            <div className={styles.controls}>
              <Button
                className={styles.confirmButton}
                text={t('confirm')}
                onClick={onCloseClick}
              />
            </div>
          )}
        </>
      )}

      {/* Entity picker — shown after asset is selected for modules with entities */}
      {showEntityPicker && (
        <>
          <div className={styles.breadcrumb}>
            {isSuperAdminMode && (
              <>
                <button type="button" className={styles.breadcrumbItem} onClick={handleBackToAssets}>
                  {t('photo_storage')}
                </button>
                <span className={styles.breadcrumbSep}>
                  <span className={styles.breadcrumbArrow}>/</span>
                  <span className={styles.breadcrumbCurrent}>{selectedAssetName}</span>
                </span>
              </>
            )}
          </div>
          <div className={styles.assetSection}>
            {entitiesLoading
              ? Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className={styles.folderShimmer} style={{ animationDelay: `${i * 0.05}s` }} />
                ))
              : entities.map(entity => (
                  <button
                    key={entity.entity_id}
                    type="button"
                    className={styles.folderCard}
                    onClick={() => handleSelectEntity(entity)}
                  >
                    <FolderIcon className={styles.folderIcon} />
                    <span className={styles.folderName}>{entity.entity_name}</span>
                    <span className={styles.folderCount}>
                      {entity.photos_count} {t('photos_short')}
                    </span>
                  </button>
                ))}
          </div>
          {selectedImages.length > 0 && (
            <div className={styles.controls}>
              <Button className={styles.confirmButton} text={t('confirm')} onClick={onCloseClick} />
            </div>
          )}
        </>
      )}

      {/* Normal picker — shown when effectiveAssetId is set and entity is selected (if required) */}
      {!showAssetPicker && !showEntityPicker && (
        <>
          {/* Breadcrumb navigation */}
          <div className={styles.breadcrumb}>
            {/* Back to assets — only for super admin */}
            {isSuperAdminMode && (
              <>
                <button
                  type="button"
                  className={styles.breadcrumbItem}
                  onClick={handleBackToAssets}
                >
                  {t('photo_storage')}
                </button>
                <span className={styles.breadcrumbSep}>
                  <span className={styles.breadcrumbArrow}>/</span>
                  {supportsEntities ? (
                    <button
                      type="button"
                      className={styles.breadcrumbItem}
                      onClick={handleBackToEntities}
                    >
                      {selectedAssetName}
                    </button>
                  ) : folderStack.length === 0 ? (
                    <span className={styles.breadcrumbCurrent}>{selectedAssetName}</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.breadcrumbItem}
                      onClick={() => navigateToBreadcrumb(0)}
                    >
                      {selectedAssetName}
                    </button>
                  )}
                </span>
              </>
            )}

            {/* Entity breadcrumb */}
            {supportsEntities && (
              <span className={styles.breadcrumbSep}>
                {!isSuperAdminMode && (
                  <button
                    type="button"
                    className={styles.breadcrumbItem}
                    onClick={handleBackToEntities}
                  >
                    {t('root_folder')}
                  </button>
                )}
                <span className={styles.breadcrumbArrow}>/</span>
                {folderStack.length === 0 ? (
                  <span className={styles.breadcrumbCurrent}>{selectedEntityName}</span>
                ) : (
                  <button
                    type="button"
                    className={styles.breadcrumbItem}
                    onClick={() => navigateToBreadcrumb(0)}
                  >
                    {selectedEntityName}
                  </button>
                )}
              </span>
            )}

            {/* Folder breadcrumbs */}
            {supportsFolders && folderStack.length > 0 && (
              <>
                {!isSuperAdminMode && !supportsEntities && (
                  <button
                    type="button"
                    className={styles.breadcrumbItem}
                    onClick={() => navigateToBreadcrumb(0)}
                  >
                    {t('root_folder')}
                  </button>
                )}
                {folderStack.map((item, index) => (
                  <span key={item.id} className={styles.breadcrumbSep}>
                    <span className={styles.breadcrumbArrow}>/</span>
                    {index < folderStack.length - 1 ? (
                      <button
                        type="button"
                        className={styles.breadcrumbItem}
                        onClick={() => navigateToBreadcrumb(index + 1)}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <span className={styles.breadcrumbCurrent}>{item.name}</span>
                    )}
                  </span>
                ))}
              </>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className={styles.hiddenInput}
          />

          {/* Folders grid */}
          {supportsFolders && (folders.length > 0 || foldersLoading) && (
            <div className={styles.folderSection}>
              {foldersLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className={styles.folderShimmer} style={{ animationDelay: `${i * 0.06}s` }} />
                  ))
                : folders.map(folder => (
                    <button
                      key={folder.id}
                      type="button"
                      className={styles.folderCard}
                      onClick={() => enterFolder(folder)}
                    >
                      <FolderIcon className={styles.folderIcon} />
                      <span className={styles.folderName}>{folder.name}</span>
                      <span className={styles.folderCount}>
                        {folder.photos_count} {t('photos_short')}
                        {folder.children_count > 0 && ` · ${folder.children_count} ${t('folders_short')}`}
                      </span>
                    </button>
                  ))}
            </div>
          )}

          {/* Photo gallery — paginated from server */}
          <div className={styles.photoPickerWrapper}>
            <PhotoPicker
              photos={allPhotos}
              selectedImages={selectedImagesWithPath}
              onImageClick={onImageChange}
              isFetching={isFetching || isLoading}
              onUploadClick={() => inputRef.current?.click()}
              isUploading={isUploading}
              uploadProgress={Math.min(Math.round(uploadProgress), 100)}
            />
          </div>

          <div className={styles.paginationContainer}>
            <Pagination
              pageCount={storagePhotos?.last_page || 1}
              onPageChange={(selectedPage: SelectedPage) => {
                setPage(selectedPage.selected + 1);
              }}
            />
          </div>

          {selectedImages.length > 0 && (
            <div className={styles.controls}>
              <Button
                className={styles.confirmButton}
                text={t('confirm')}
                onClick={onCloseClick}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoPickerWithUpload;
